import { useCallback, useEffect, useState } from 'react';
import { authHeader } from '../lib/session.js';

/* Free-trial sign-ups, for administrators.
 *
 * Everyone who has filled in the Free Trial form on sentripol.ai: one row per
 * email address, newest first. Someone who submitted more than once is one
 * row, with the count. A page of its own, shown by the "Leads" tab. It
 * reloads each time the tab is opened, and every 30 seconds while it is
 * showing, so a sign-up made while it is open turns up without a reload.
 */

const POLL_MS = 30_000;

const base = () => {
  const b = ((typeof window !== 'undefined' && window.SENTRIPOL_CONFIG) || {}).backend;
  return (b == null ? '' : b).replace(/\/+$/, '');
};

async function fetchLeads() {
  const res = await fetch(base() + '/api/leads', { headers: await authHeader() });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) throw new Error('Your session has expired. Reload the page to sign in again.');
  if (res.status === 403) throw new Error('Leads are available to administrators only.');
  if (!res.ok) throw new Error(data.error || `Could not load leads (${res.status}).`);
  return data;
}

const when = (iso) => new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });

/* A spreadsheet-safe CSV: quoted fields, and a leading ' on anything a
 * spreadsheet would read as a formula, since every value here was typed by a
 * member of the public. */
function toCsv(rows) {
  const cell = (v) => {
    let s = v == null ? '' : String(v);
    if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
    return '"' + s.replace(/"/g, '""') + '"';
  };
  const head = ['Name', 'Work email', 'Agency', 'Signed up', 'Last submitted', 'Submissions'];
  const body = rows.map((r) => [r.name, r.email, r.agency, r.created_at, r.last_seen_at, r.submissions].map(cell).join(','));
  return [head.map(cell).join(','), ...body].join('\r\n');
}

function download(rows) {
  const url = URL.createObjectURL(new Blob([toCsv(rows)], { type: 'text/csv' }));
  const a = Object.assign(document.createElement('a'), { href: url, download: `sentripol-leads-${new Date().toISOString().slice(0, 10)}.csv` });
  a.click();
  URL.revokeObjectURL(url);
}

export default function LeadsPage({ active }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await fetchLeads()); setError(null); } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [active, load]);

  return (
    <main id="spLeadsPage" className="sp-cost-page" role="tabpanel" aria-labelledby="spTabLeads" hidden={!active}>
      <div className="sp-admin-card sp-cost-card">
        <div className="sp-leads-head">
          <h2>Free-trial leads</h2>
          <div className="sp-leads-actions">
            <button type="button" className="btn btn-outline btn-sm" onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
            <button type="button" className="btn btn-outline btn-sm" onClick={() => download(data.rows)} disabled={!data || !data.rows.length}>Download CSV</button>
          </div>
        </div>
        <div className="sp-admin-sub">Everyone who has filled in the Free Trial form on sentripol.ai, newest first.</div>

        {error ? <div className="sp-cost-alert">{error}</div> : null}

        {data ? (
          <section className="sp-admin-section">
            <h3>{data.total.toLocaleString('en-US')} {data.total === 1 ? 'lead' : 'leads'}</h3>
            <div className="sp-cost-scroll">
              <table className="sp-cost-table sp-leads-table">
                <thead><tr>
                  <th>Name</th><th>Work email</th><th>Agency</th><th>Signed up</th><th>Submissions</th>
                </tr></thead>
                <tbody>
                  {data.rows.length ? data.rows.map((r) => (
                    <tr key={r.email}>
                      <td>{r.name}</td>
                      <td><a href={'mailto:' + r.email}>{r.email}</a></td>
                      <td>{r.agency || '—'}</td>
                      <td title={r.submissions > 1 ? `Last submitted ${when(r.last_seen_at)}` : undefined}>{when(r.created_at)}</td>
                      <td>{r.submissions}</td>
                    </tr>
                  )) : <tr><td colSpan="5" className="sp-cost-empty">No sign-ups yet.</td></tr>}
                </tbody>
              </table>
            </div>
            {data.total > data.rows.length ? (
              <p className="sp-cost-note">Showing the newest {data.rows.length.toLocaleString('en-US')}.</p>
            ) : null}
          </section>
        ) : null}
      </div>
    </main>
  );
}
